import React, { useState } from "react";

// ─── Design Tokens ───────────────────────────────────────────────
const colors = {
  pixleyBlue: "#0065F8",
  skySpark: "#00CAFF",
  dreamMist: "#7FC6FF",
  cloudWhite: "#E1EBFF",
  inkNavy: "#0B1A33",
  deepBlue: "#073B8E",
  slateBlue: "#334155",
  mistGray: "#D7E3F3",
  softWhite: "#FFFDF7",
};

const cardFills = {
  paleBlue: "#EAF3FF",
  cream: "#FFF8E7",
  softPink: "#FFE9EE",
  mint: "#E6F7EE",
};

const badgeVariants = [
  { label: "Published", fill: "#3EDC8F", text: "#0B1A33" },
  { label: "Draft", fill: "#D7E3F3", text: "#334155" },
  { label: "In Review", fill: "#FFD76A", text: "#0B1A33" },
  { label: "Archived", fill: "#CBD5E1", text: "#334155" },
  { label: "Scheduled", fill: "#7FC6FF", text: "#0B1A33" },
  { label: "Most Popular", fill: "#FF6B9A", text: "#FFFFFF" },
  { label: "New", fill: "#0065F8", text: "#FFFFFF" },
  { label: "Featured", fill: "#FFD76A", text: "#0B1A33" },
];

const contentTags = ["Art", "Animation", "Story", "Music", "Game", "3D", "Coding", "Education"];

// ─── Reusable Style Objects ──────────────────────────────────────

const cardShadow = "0px 2px 0px rgba(11,26,51,0.08), 0px 10px 24px rgba(11,26,51,0.10)";
const raisedShadow = "0px 3px 0px rgba(11,26,51,0.10), 0px 16px 36px rgba(11,26,51,0.14)";

const primaryBtnStyle = {
  background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
  color: "#FFF",
  borderRadius: 18,
  boxShadow: "0px 4px 0px #073B8E",
  padding: "0 22px",
  height: 48,
  fontFamily: "'Neue Montreal', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  border: "none",
  cursor: "pointer",
  transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
};

const secondaryBtnStyle = {
  background: "#FFFDF7",
  color: "#0065F8",
  border: "2px solid rgba(0,101,248,0.18)",
  borderRadius: 18,
  boxShadow: "0px 4px 0px #C8DDF7",
  height: 48,
  padding: "0 22px",
  fontFamily: "'Neue Montreal', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
};

const ghostBtnStyle = {
  background: "transparent",
  color: "#0065F8",
  border: "none",
  fontFamily: "'Neue Montreal', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  padding: "0 8px",
  height: 48,
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

const destructiveBtnStyle = {
  background: "linear-gradient(180deg, #FF6B6B 0%, #E53E3E 100%)",
  color: "#FFF",
  borderRadius: 18,
  boxShadow: "0px 4px 0px #C53030",
  padding: "0 22px",
  height: 48,
  fontFamily: "'Neue Montreal', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  border: "none",
  cursor: "pointer",
  transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
};

const inputStyle = {
  height: 48,
  borderRadius: 14,
  background: "#FFFDF7",
  border: "2px solid rgba(11,26,51,0.08)",
  boxShadow: "inset 0px 2px 4px rgba(11,26,51,0.06), 0px 2px 0px rgba(11,26,51,0.08)",
  padding: "0 16px",
  fontFamily: "'Neue Montreal', sans-serif",
  fontSize: 15,
  color: "#0B1A33",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// ─── Sidebar navigation items ────────────────────────────────────
const sidebarItems = [
  { id: "overview", label: "Overview", icon: "star" },
  { id: "brand", label: "Brand", icon: "palette" },
  { id: "colors", label: "Colors", icon: "droplet" },
  { id: "typography", label: "Typography", icon: "type" },
  { id: "buttons", label: "Buttons", icon: "mouse-pointer" },
  { id: "inputs", label: "Inputs & Forms", icon: "edit" },
  { id: "cards", label: "Cards", icon: "layout" },
  { id: "navigation", label: "Navigation", icon: "menu" },
  { id: "modals", label: "Modals & Overlays", icon: "layers" },
  { id: "chips", label: "Chips & Badges", icon: "tag" },
  { id: "motion", label: "Motion", icon: "zap" },
  { id: "illustration", label: "Illustration", icon: "image" },
];

// Simple SVG icons for sidebar
const SidebarIcon = ({ name, color = "currentColor", size = 18 }) => {
  const icons = {
    star: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01z" />,
    palette: <><path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10c.55 0 1-.45 1-1v-1.26c0-.55-.45-1-1-1H9c-1.1 0-2-.9-2-2v-1c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v1" /><circle cx="7.5" cy="11.5" r="1.5" /><circle cx="12" cy="7.5" r="1.5" /><circle cx="16.5" cy="11.5" r="1.5" /></>,
    droplet: <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />,
    type: <><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></>,
    "mouse-pointer": <><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51z" /><line x1="13" y1="13" x2="19" y2="19" /></>,
    edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
    layout: <><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>,
    menu: <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>,
    layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
    tag: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>,
    zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

// ─── Bento Card wrapper ──────────────────────────────────────────
const BentoCard = ({ children, fill = cardFills.paleBlue, style = {}, className = "", rotation = 0 }) => (
  <div
    className={className}
    style={{
      background: fill,
      borderRadius: 24,
      padding: 24,
      boxShadow: cardShadow,
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      transition: "transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms cubic-bezier(.2,.8,.2,1)",
      ...style,
    }}
  >
    {children}
  </div>
);

// ─── Eyebrow label ───────────────────────────────────────────────
const Eyebrow = ({ children, style = {} }) => (
  <span
    style={{
      fontFamily: "'Neue Montreal', sans-serif",
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: colors.slateBlue,
      ...style,
    }}
  >
    {children}
  </span>
);

// ─── Section Title ───────────────────────────────────────────────
const SectionTitle = ({ children, style = {} }) => (
  <h2
    style={{
      fontFamily: "'Fredoka', sans-serif",
      fontSize: 36,
      fontWeight: 700,
      lineHeight: 0.95,
      letterSpacing: "-0.01em",
      color: colors.inkNavy,
      margin: 0,
      ...style,
    }}
  >
    {children}
  </h2>
);

// ─── Color Swatch ────────────────────────────────────────────────
const ColorSwatch = ({ hex, label, size = 56, dark = false }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: hex,
        boxShadow: "0px 2px 8px rgba(11,26,51,0.15)",
        border: dark ? "none" : "2px solid rgba(255,255,255,0.6)",
      }}
    />
    <span style={{ fontFamily: "'Neue Montreal', sans-serif", fontSize: 11, fontWeight: 700, color: colors.slateBlue }}>
      {hex}
    </span>
    {label && (
      <span style={{ fontFamily: "'Neue Montreal', sans-serif", fontSize: 10, color: colors.slateBlue, opacity: 0.7 }}>
        {label}
      </span>
    )}
  </div>
);

// ─── Sticker Badge ───────────────────────────────────────────────
const StickerBadge = ({ label, fill, text, rotation = 0 }) => (
  <span
    style={{
      display: "inline-block",
      borderRadius: 999,
      padding: "7px 12px",
      fontFamily: "'Neue Montreal', sans-serif",
      fontSize: 13,
      fontWeight: 700,
      boxShadow: "0px 3px 0px rgba(11,26,51,0.16)",
      background: fill,
      color: text,
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

// ─── Icon Button ─────────────────────────────────────────────────
const IconBtn = ({ children, variant = "blue" }) => {
  const fills = {
    blue: { bg: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", color: "#FFF", shadow: "0px 3px 0px #073B8E" },
    sky: { bg: "#00CAFF", color: "#0B1A33", shadow: "0px 3px 0px #0096C7" },
    cream: { bg: "#FFD76A", color: "#0B1A33", shadow: "0px 3px 0px #D39B22" },
    pink: { bg: "#FF6B9A", color: "#FFF", shadow: "0px 3px 0px #C94D73" },
    soft: { bg: "#FFFDF7", color: "#0065F8", shadow: "0px 3px 0px rgba(11,26,51,0.18)" },
  };
  const f = fills[variant] || fills.blue;
  return (
    <button
      style={{
        width: 40,
        height: 40,
        borderRadius: 14,
        background: f.bg,
        color: f.color,
        boxShadow: f.shadow,
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        fontWeight: 700,
        transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      {children}
    </button>
  );
};

// ─── Toggle ──────────────────────────────────────────────────────
const Toggle = ({ checked = false }) => (
  <div
    style={{
      width: 48,
      height: 28,
      borderRadius: 999,
      background: checked ? colors.pixleyBlue : colors.mistGray,
      position: "relative",
      cursor: "pointer",
      transition: "background 160ms ease",
    }}
  >
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: "#FFF",
        boxShadow: "0px 2px 4px rgba(11,26,51,0.20)",
        position: "absolute",
        top: 2,
        left: checked ? 22 : 2,
        transition: "left 160ms ease",
      }}
    />
  </div>
);

// ═════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════
const DesignSystem = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [toggleOn, setToggleOn] = useState(true);
  const [checkboxOn, setCheckboxOn] = useState(true);
  const [radioVal, setRadioVal] = useState("public");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("For You");
  const [activeFilterChip, setActiveFilterChip] = useState("All");

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`ds-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        display: "flex",
        fontFamily: "'Neue Montreal', sans-serif",
      }}
    >
      {/* ── Sidebar ───────────────────────────────────────────── */}
      <aside
        style={{
          width: 220,
          minHeight: "100vh",
          background: "#F7FBFF",
          padding: "24px 12px",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 8px", marginBottom: 20 }}>
          <img src="/assets/pixley-panda.png" alt="Pixley" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <span
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: colors.inkNavy,
              letterSpacing: "-0.02em",
            }}
          >
            PIXLEY
          </span>
        </div>

        {sidebarItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 44,
                padding: "0 12px",
                borderRadius: 14,
                border: "none",
                cursor: "pointer",
                background: isActive ? colors.pixleyBlue : "transparent",
                color: isActive ? "#FFF" : colors.slateBlue,
                boxShadow: isActive ? "0px 4px 0px #073B8E" : "none",
                fontFamily: "'Neue Montreal', sans-serif",
                fontSize: 14,
                fontWeight: isActive ? 700 : 500,
                transition: "all 120ms ease",
                width: "100%",
                textAlign: "left",
              }}
            >
              <SidebarIcon name={item.icon} color={isActive ? "#FFF" : colors.slateBlue} />
              {item.label}
            </button>
          );
        })}

        {/* Version badge at bottom */}
        <div style={{ marginTop: "auto", padding: "16px 8px 0" }}>
          <div style={{ fontSize: 11, color: colors.slateBlue, opacity: 0.6, lineHeight: 1.5 }}>
            Design System<br />v1.0.0<br />May 2026
          </div>
        </div>
      </aside>

      {/* ── Main Content ──────────────────────────────────────── */}
      <main
        style={{
          marginLeft: 220,
          flex: 1,
          maxWidth: 1440,
          padding: "32px 24px 80px",
        }}
      >
        {/* Page title */}
        <h1
          style={{
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 64,
            fontWeight: 700,
            lineHeight: 0.9,
            letterSpacing: "-0.02em",
            color: colors.inkNavy,
            margin: "0 0 32px 0",
            textTransform: "uppercase",
          }}
        >
          Pixley Design System
        </h1>

        {/* ════════════════════════════════════════════════════ */}
        {/* BENTO GRID                                          */}
        {/* ════════════════════════════════════════════════════ */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 16,
          }}
        >
          {/* ── OVERVIEW / HERO ─── span 6 ────────────────── */}
          <BentoCard
            id="ds-overview"
            fill={cardFills.cream}
            style={{ gridColumn: "span 6", minHeight: 320, display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative", overflow: "hidden" }}
            className="ds-hero"
          >
            <div style={{ position: "relative", zIndex: 1 }}>
              <span style={{ fontFamily: "'Caveat', 'Patrick Hand', cursive", fontSize: 20, color: colors.pixleyBlue, fontWeight: 700 }}>
                our why
              </span>
              <h2
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 52,
                  fontWeight: 700,
                  lineHeight: 0.92,
                  letterSpacing: "-0.015em",
                  color: colors.inkNavy,
                  margin: "8px 0 16px",
                  textTransform: "uppercase",
                }}
              >
                Make Every<br />Kid a Creator
              </h2>
              <p style={{ fontSize: 15, lineHeight: 1.45, color: colors.slateBlue, maxWidth: 380, margin: 0 }}>
                Pixley empowers kids to imagine, create, and share — safely and joyfully. Our design system is playful, tactile, and built for young makers.
              </p>
              <div style={{ display: "flex", gap: 24, marginTop: 20 }}>
                {[
                  { emoji: "\u2764\uFE0F", label: "Joyful & Playful" },
                  { emoji: "\uD83D\uDEE1\uFE0F", label: "Safe by Design" },
                  { emoji: "\uD83C\uDFA8", label: "Empowering Creativity" },
                  { emoji: "\u2B50", label: "Inclusive & Encouraging" },
                ].map((v) => (
                  <div key={v.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: colors.slateBlue }}>
                    <span style={{ fontSize: 16 }}>{v.emoji}</span>
                    {v.label}
                  </div>
                ))}
              </div>
            </div>
            {/* Decorative handwritten note */}
            <div
              style={{
                position: "absolute",
                top: 24,
                right: 24,
                fontFamily: "'Caveat', 'Patrick Hand', cursive",
                fontSize: 16,
                color: colors.slateBlue,
                background: "#FFFDF7",
                borderRadius: 16,
                padding: "12px 16px",
                boxShadow: "0px 2px 8px rgba(11,26,51,0.08)",
                transform: "rotate(1.5deg)",
                maxWidth: 140,
                lineHeight: 1.3,
              }}
            >
              Design that feels like play. Built for creators.
            </div>
          </BentoCard>

          {/* ── BRAND ─── span 3 ──────────────────────────── */}
          <BentoCard
            id="ds-brand"
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 3", minHeight: 320 }}
          >
            <Eyebrow>Brand</Eyebrow>
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue, margin: "0 0 12px" }}>Primary Logo</p>
              <div style={{ background: "#FFF", borderRadius: 18, padding: 16, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset 0 1px 3px rgba(11,26,51,0.06)" }}>
                <img src="/assets/pixley-logo-full.png" alt="Pixley Logo" style={{ height: 80, objectFit: "contain" }} />
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: colors.slateBlue, margin: "0 0 8px" }}>App Icon</p>
                  <div style={{ background: "#FFF", borderRadius: 14, padding: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src="/assets/pixley-panda.png" alt="App Icon" style={{ height: 48, objectFit: "contain" }} />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: colors.slateBlue, margin: "0 0 8px" }}>Logo Mark</p>
                  <div style={{ background: colors.pixleyBlue, borderRadius: 14, padding: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src="/assets/pixley-panda.png" alt="Logo Mark" style={{ height: 48, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>

          {/* ── COLOR SYSTEM ─── span 3 ───────────────────── */}
          <BentoCard
            id="ds-colors"
            fill={cardFills.softPink}
            style={{ gridColumn: "span 3", minHeight: 320 }}
          >
            <Eyebrow>Color System</Eyebrow>
            <p style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue, margin: "16px 0 10px" }}>Main Palette</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <ColorSwatch hex="#0065F8" label="Pixley Blue" dark />
              <ColorSwatch hex="#00CAFF" label="Sky Spark" dark />
              <ColorSwatch hex="#7FC6FF" label="Dream Mist" />
              <ColorSwatch hex="#E1EBFF" label="Cloud White" />
              <ColorSwatch hex="#0B1A33" label="Ink Navy" dark />
            </div>
            <p style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue, margin: "16px 0 10px" }}>Soft Card Fills</p>
            <div style={{ display: "flex", gap: 8 }}>
              {Object.entries(cardFills).map(([name, hex]) => (
                <div key={name} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: "100%", height: 36, borderRadius: 10, background: hex, border: "1px solid rgba(11,26,51,0.06)" }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: colors.slateBlue }}>{hex}</span>
                  <span style={{ fontSize: 9, color: colors.slateBlue, opacity: 0.6, textTransform: "capitalize" }}>{name.replace(/([A-Z])/g, " $1").trim()}</span>
                </div>
              ))}
            </div>
          </BentoCard>

          {/* ── TYPOGRAPHY ─── span 4 ─────────────────────── */}
          <BentoCard
            id="ds-typography"
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 4", minHeight: 250 }}
          >
            <Eyebrow>Typography</Eyebrow>
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue }}>Burbank Big Condensed</span>
                <span style={{ fontSize: 10, color: colors.slateBlue, opacity: 0.6, marginLeft: 8 }}>(Headlines Only)</span>
              </div>
              <h3
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 48,
                  fontWeight: 700,
                  lineHeight: 0.92,
                  letterSpacing: "-0.015em",
                  color: colors.inkNavy,
                  margin: "0 0 12px",
                  textTransform: "uppercase",
                }}
              >
                Hero Headline
              </h3>

              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue }}>Neue Montreal</span>
              </div>
              <p style={{ fontSize: 18, fontWeight: 700, color: colors.inkNavy, margin: "0 0 4px", letterSpacing: "-0.01em" }}>
                Subheadline Example
              </p>
              <p style={{ fontSize: 15, fontWeight: 500, color: colors.slateBlue, margin: "0 0 12px", lineHeight: 1.45 }}>
                This is body copy set in Neue Montreal. It's clean, friendly, and highly legible for kids and parents.
              </p>

              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue }}>Caveat</span>
                <span style={{ fontSize: 10, color: colors.slateBlue, opacity: 0.6, marginLeft: 8 }}>(Handwritten Accent)</span>
              </div>
              <p style={{ fontFamily: "'Caveat', 'Patrick Hand', cursive", fontSize: 20, fontWeight: 700, color: colors.pixleyBlue, margin: 0 }}>
                This is a handwritten note for playful callouts and special moments!
              </p>
            </div>
          </BentoCard>

          {/* ── BUTTONS ─── span 4 ────────────────────────── */}
          <BentoCard
            id="ds-buttons"
            fill={cardFills.cream}
            style={{ gridColumn: "span 4", minHeight: 250 }}
          >
            <Eyebrow>Buttons</Eyebrow>
            <div style={{ marginTop: 16 }}>
              {/* Button state labels */}
              <div style={{ display: "grid", gridTemplateColumns: "80px repeat(4, 1fr)", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <span />
                {["Default", "Hover", "Pressed", "Disabled"].map((s) => (
                  <span key={s} style={{ fontSize: 11, fontWeight: 600, color: colors.slateBlue, textAlign: "center" }}>{s}</span>
                ))}
              </div>

              {/* Primary row */}
              <div style={{ display: "grid", gridTemplateColumns: "80px repeat(4, 1fr)", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue }}>Primary</span>
                <button style={{ ...primaryBtnStyle, height: 40, fontSize: 13 }}>Create</button>
                <button style={{ ...primaryBtnStyle, height: 40, fontSize: 13, transform: "translateY(-1px)", boxShadow: "0px 5px 0px #073B8E" }}>Create</button>
                <button style={{ ...primaryBtnStyle, height: 40, fontSize: 13, transform: "translateY(4px)", boxShadow: "0px 0px 0px #073B8E" }}>Create</button>
                <button style={{ ...primaryBtnStyle, height: 40, fontSize: 13, background: "#D7E3F3", color: "#8CA0BC", boxShadow: "0px 4px 0px #B9C8DC", cursor: "not-allowed" }}>Create</button>
              </div>

              {/* Secondary row */}
              <div style={{ display: "grid", gridTemplateColumns: "80px repeat(4, 1fr)", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue }}>Secondary</span>
                <button style={{ ...secondaryBtnStyle, height: 40, fontSize: 13 }}>Explore</button>
                <button style={{ ...secondaryBtnStyle, height: 40, fontSize: 13, transform: "translateY(-1px)" }}>Explore</button>
                <button style={{ ...secondaryBtnStyle, height: 40, fontSize: 13, transform: "translateY(4px)", boxShadow: "none" }}>Explore</button>
                <button style={{ ...secondaryBtnStyle, height: 40, fontSize: 13, opacity: 0.5, cursor: "not-allowed" }}>Explore</button>
              </div>

              {/* Ghost row */}
              <div style={{ display: "grid", gridTemplateColumns: "80px repeat(4, 1fr)", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue }}>Ghost</span>
                <button style={{ ...ghostBtnStyle, height: 40, fontSize: 13 }}>Learn More</button>
                <button style={{ ...ghostBtnStyle, height: 40, fontSize: 13, color: "#073B8E" }}>Learn More</button>
                <button style={{ ...ghostBtnStyle, height: 40, fontSize: 13, color: "#073B8E" }}>Learn More</button>
                <button style={{ ...ghostBtnStyle, height: 40, fontSize: 13, opacity: 0.4, cursor: "not-allowed" }}>Learn More</button>
              </div>

              {/* Destructive row */}
              <div style={{ display: "grid", gridTemplateColumns: "80px repeat(4, 1fr)", gap: 8, alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue }}>Destructive</span>
                <button style={{ ...destructiveBtnStyle, height: 40, fontSize: 13 }}>Delete</button>
                <button style={{ ...destructiveBtnStyle, height: 40, fontSize: 13, transform: "translateY(-1px)", boxShadow: "0px 5px 0px #C53030" }}>Delete</button>
                <button style={{ ...destructiveBtnStyle, height: 40, fontSize: 13, transform: "translateY(4px)", boxShadow: "0px 0px 0px #C53030" }}>Delete</button>
                <button style={{ ...destructiveBtnStyle, height: 40, fontSize: 13, opacity: 0.5, cursor: "not-allowed" }}>Delete</button>
              </div>

              {/* Icon buttons */}
              <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 8 }}>Icon Buttons</span>
              <div style={{ display: "flex", gap: 8 }}>
                <IconBtn variant="blue">+</IconBtn>
                <IconBtn variant="sky">+</IconBtn>
                <IconBtn variant="cream">{"\u2605"}</IconBtn>
                <IconBtn variant="pink">{"\u2764"}</IconBtn>
                <IconBtn variant="soft">{"\u2191"}</IconBtn>
                <IconBtn variant="soft">{"\u2026"}</IconBtn>
              </div>
            </div>
          </BentoCard>

          {/* ── INPUTS & FORMS ─── span 4 ─────────────────── */}
          <BentoCard
            id="ds-inputs"
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 4", minHeight: 250 }}
          >
            <Eyebrow>Inputs & Forms</Eyebrow>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {/* Left column — text inputs */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 6 }}>Text Input</label>
                  <input type="text" placeholder="Enter your project name..." style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 6 }}>Search</label>
                  <div style={{ position: "relative" }}>
                    <input type="text" placeholder="Search projects..." style={{ ...inputStyle, paddingLeft: 40 }} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.slateBlue} strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", left: 14, top: 16 }}>
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 6 }}>Textarea</label>
                  <textarea
                    placeholder="Tell us about your amazing idea... You can write anything!"
                    style={{
                      ...inputStyle,
                      height: "auto",
                      minHeight: 80,
                      borderRadius: 18,
                      padding: 16,
                      resize: "vertical",
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 6 }}>Select</label>
                  <select style={{ ...inputStyle, appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%230065F8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", paddingRight: 40 }}>
                    <option>Choose a category</option>
                  </select>
                </div>
              </div>

              {/* Right column — controls */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 8 }}>Toggle</label>
                  <div onClick={() => setToggleOn(!toggleOn)} style={{ cursor: "pointer" }}>
                    <Toggle checked={toggleOn} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 8 }}>Checkbox</label>
                  <div
                    onClick={() => setCheckboxOn(!checkboxOn)}
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        background: checkboxOn ? colors.pixleyBlue : "#FFF",
                        border: checkboxOn ? "none" : `2px solid ${colors.dreamMist}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 120ms ease",
                      }}
                    >
                      {checkboxOn && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 500, color: colors.inkNavy }}>Allow comments</span>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 8 }}>Radio</label>
                  {["Public", "Private"].map((opt) => (
                    <div
                      key={opt}
                      onClick={() => setRadioVal(opt.toLowerCase())}
                      style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 6 }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: `2px solid ${radioVal === opt.toLowerCase() ? colors.pixleyBlue : colors.dreamMist}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 120ms ease",
                        }}
                      >
                        {radioVal === opt.toLowerCase() && (
                          <div style={{ width: 10, height: 10, borderRadius: "50%", background: colors.pixleyBlue }} />
                        )}
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: colors.inkNavy }}>{opt}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, display: "block", marginBottom: 8 }}>Upload</label>
                  <div
                    style={{
                      border: "2px dashed rgba(0,101,248,0.25)",
                      borderRadius: 18,
                      padding: "16px 12px",
                      textAlign: "center",
                      background: "rgba(234,243,255,0.5)",
                      cursor: "pointer",
                    }}
                  >
                    <p style={{ fontSize: 13, fontWeight: 600, color: colors.pixleyBlue, margin: 0 }}>Drop files here</p>
                    <p style={{ fontSize: 11, color: colors.slateBlue, margin: "4px 0 0" }}>or click to upload</p>
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>

          {/* ── CARDS ─── span 5 ──────────────────────────── */}
          <BentoCard
            id="ds-cards"
            fill={cardFills.cream}
            style={{ gridColumn: "span 8", minHeight: 280 }}
          >
            <Eyebrow>Cards</Eyebrow>
            <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
              {/* Media Card */}
              <div style={{ background: cardFills.paleBlue, borderRadius: 20, padding: 12, width: 170, boxShadow: cardShadow }}>
                <div style={{ background: "linear-gradient(135deg, #0065F8, #00CAFF)", borderRadius: 14, height: 100, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: "#FFF", fontSize: 16 }}>{"\u25B6"}</span>
                  </div>
                </div>
                <p style={{ fontSize: 14, fontWeight: 700, color: colors.inkNavy, margin: "10px 0 2px" }}>My Dream World</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: colors.slateBlue }}>by Luna</span>
                  <span style={{ fontSize: 11, color: colors.slateBlue }}>{"\u2764"} 128</span>
                </div>
              </div>

              {/* Stats Card */}
              <div style={{ background: cardFills.mint, borderRadius: 20, padding: 20, width: 140, boxShadow: cardShadow, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 4 }}>{"\uD83C\uDFA8"}</div>
                <span
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    fontSize: 40,
                    fontWeight: 700,
                    color: colors.inkNavy,
                    lineHeight: 1,
                  }}
                >
                  2,847
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue, marginTop: 4 }}>Projects Created</span>
                <span style={{ fontSize: 11, color: "#20A96A", fontWeight: 700, marginTop: 4 }}>+24% this week</span>
              </div>

              {/* Safety Card */}
              <div style={{ background: cardFills.paleBlue, borderRadius: 20, padding: 20, width: 160, boxShadow: cardShadow }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E6F7EE", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>{"\u2705"}</span>
                </div>
                <p style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: colors.inkNavy, margin: "0 0 6px", textTransform: "uppercase" }}>Always Safe</p>
                <p style={{ fontSize: 12, color: colors.slateBlue, margin: 0, lineHeight: 1.4 }}>
                  Pixley is designed with kid safety as our top priority.
                </p>
                <button style={{ ...ghostBtnStyle, height: "auto", padding: "6px 0", fontSize: 12, marginTop: 8 }}>
                  Learn more {"\u2192"}
                </button>
              </div>

              {/* Pricing Card */}
              <div style={{ background: cardFills.softPink, borderRadius: 20, padding: 20, width: 170, boxShadow: cardShadow, position: "relative" }}>
                <div style={{ position: "absolute", top: -6, right: 12 }}>
                  <StickerBadge label="Most Popular" fill="#FF6B9A" text="#FFF" rotation={-2} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: colors.slateBlue, margin: "12px 0 4px", letterSpacing: "0.04em", textTransform: "uppercase" }}>Creator Pro</p>
                <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                  <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 36, fontWeight: 700, color: colors.inkNavy }}>$4.99</span>
                  <span style={{ fontSize: 13, color: colors.slateBlue }}>/ month</span>
                </div>
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  {["Unlimited projects", "HD exports", "Priority support"].map((f) => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: colors.slateBlue }}>
                      <span style={{ color: "#3EDC8F" }}>{"\u2713"}</span> {f}
                    </div>
                  ))}
                </div>
                <button
                  style={{
                    ...primaryBtnStyle,
                    width: "100%",
                    height: 40,
                    fontSize: 13,
                    marginTop: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Get Started
                </button>
              </div>
            </div>
          </BentoCard>

          {/* ── NAVIGATION ─── span 4 ─────────────────────── */}
          <BentoCard
            id="ds-navigation"
            fill={cardFills.mint}
            style={{ gridColumn: "span 4", minHeight: 220 }}
          >
            <Eyebrow>Navigation</Eyebrow>
            <div style={{ marginTop: 16 }}>
              {/* Top nav mockup */}
              <div
                style={{
                  height: 48,
                  borderRadius: 20,
                  background: colors.softWhite,
                  boxShadow: "0px 4px 0px rgba(11,26,51,0.10), 0px 12px 28px rgba(11,26,51,0.10)",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                  gap: 6,
                }}
              >
                <img src="/assets/pixley-panda.png" alt="Pixley" style={{ width: 28, height: 28, borderRadius: 8, marginRight: 4 }} />
                {["Home", "Discover", "Create", "Activity"].map((tab) => (
                  <button
                    key={tab}
                    style={{
                      height: 34,
                      padding: "0 12px",
                      borderRadius: 12,
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "'Neue Montreal', sans-serif",
                      fontSize: 13,
                      fontWeight: 700,
                      background: tab === "Create" ? colors.pixleyBlue : "transparent",
                      color: tab === "Create" ? "#FFF" : colors.slateBlue,
                      boxShadow: tab === "Create" ? "0px 3px 0px #073B8E" : "none",
                      transition: "all 120ms ease",
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tabs / Segmented control */}
              <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "16px 0 8px" }}>Tabs</p>
              <div
                style={{
                  background: colors.softWhite,
                  borderRadius: 16,
                  padding: 4,
                  display: "inline-flex",
                  gap: 2,
                }}
              >
                {["For You", "Projects", "Templates", "Likes"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      height: 36,
                      padding: "0 14px",
                      borderRadius: 12,
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "'Neue Montreal', sans-serif",
                      fontSize: 13,
                      fontWeight: 700,
                      background: activeTab === tab ? colors.pixleyBlue : "transparent",
                      color: activeTab === tab ? "#FFF" : colors.slateBlue,
                      boxShadow: activeTab === tab ? "0px 3px 0px #073B8E" : "none",
                      transition: "all 120ms ease",
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Breadcrumbs */}
              <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "16px 0 8px" }}>Breadcrumbs</p>
              <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <span style={{ color: colors.pixleyBlue, fontWeight: 600, cursor: "pointer" }}>Home</span>
                <span style={{ color: colors.slateBlue, opacity: 0.4 }}>&gt;</span>
                <span style={{ color: colors.pixleyBlue, fontWeight: 600, cursor: "pointer" }}>Projects</span>
                <span style={{ color: colors.slateBlue, opacity: 0.4 }}>&gt;</span>
                <span style={{ color: colors.inkNavy, fontWeight: 700 }}>My Dream World</span>
              </div>
            </div>
          </BentoCard>

          {/* ── MODAL / OVERLAY ─── span 4 ────────────────── */}
          <BentoCard
            id="ds-modals"
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 4", minHeight: 220, position: "relative" }}
          >
            <Eyebrow>Modal / Overlay</Eyebrow>
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setShowModal(true)} style={{ ...primaryBtnStyle, height: 40, fontSize: 13 }}>
                Open Modal
              </button>

              {/* Mini modal preview */}
              <div
                style={{
                  marginTop: 16,
                  background: cardFills.cream,
                  borderRadius: 28,
                  padding: 24,
                  boxShadow: "0px 5px 0px rgba(11,26,51,0.12), 0px 24px 64px rgba(11,26,51,0.24)",
                  position: "relative",
                  maxWidth: 280,
                }}
              >
                <button
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: colors.softWhite,
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0px 3px 0px rgba(11,26,51,0.14)",
                    fontSize: 14,
                    color: colors.slateBlue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {"\u2715"}
                </button>
                <h3
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    fontSize: 28,
                    fontWeight: 700,
                    lineHeight: 0.95,
                    color: colors.inkNavy,
                    margin: "0 0 8px",
                    textTransform: "uppercase",
                  }}
                >
                  Amazing job!
                </h3>
                <p style={{ fontSize: 14, color: colors.slateBlue, lineHeight: 1.45, margin: "0 0 16px" }}>
                  Your project has been published and shared.
                </p>
                <button style={{ ...primaryBtnStyle, width: "100%", height: 44, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  View Project
                </button>
              </div>
            </div>
          </BentoCard>

          {/* ── CHIPS & BADGES ─── span 6 ─────────────────── */}
          <BentoCard
            id="ds-chips"
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 6", minHeight: 220 }}
          >
            <Eyebrow>Chips & Badges</Eyebrow>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 20 }}>
              {/* Status badges */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "0 0 10px" }}>Status Badges</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {badgeVariants.slice(0, 5).map((b) => (
                    <StickerBadge key={b.label} {...b} />
                  ))}
                </div>
              </div>

              {/* Content Tags */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "0 0 10px" }}>Content Tags</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {contentTags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: "inline-block",
                        borderRadius: 999,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        background: colors.cloudWhite,
                        color: colors.inkNavy,
                        boxShadow: "0px 2px 0px rgba(11,26,51,0.10)",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Special badges */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "0 0 10px" }}>Special Badges</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                  <StickerBadge label="FEATURED" fill="#FFD76A" text="#0B1A33" rotation={-1} />
                  <StickerBadge label="NEW!" fill="#0065F8" text="#FFF" rotation={1} />
                  <StickerBadge label="MOST POPULAR" fill="#FF6B9A" text="#FFF" rotation={-2} />
                </div>
              </div>

              {/* Filter chips */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: colors.slateBlue, margin: "0 0 10px" }}>Filter Chips</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["All", "Trending", "Following", "Recently Added"].map((chip) => (
                    <button
                      key={chip}
                      onClick={() => setActiveFilterChip(chip)}
                      style={{
                        borderRadius: 999,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        background: activeFilterChip === chip ? colors.pixleyBlue : colors.cloudWhite,
                        color: activeFilterChip === chip ? "#FFF" : colors.inkNavy,
                        border: "none",
                        cursor: "pointer",
                        boxShadow: activeFilterChip === chip ? "0px 3px 0px #073B8E" : "0px 2px 0px rgba(11,26,51,0.10)",
                        transition: "all 120ms ease",
                      }}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </BentoCard>

          {/* ── ILLUSTRATION & DOODLES ─── span 6 ─────────── */}
          <BentoCard
            id="ds-illustration"
            fill={cardFills.softPink}
            style={{ gridColumn: "span 6", minHeight: 220, position: "relative", overflow: "hidden" }}
          >
            <Eyebrow>Illustration & Doodles</Eyebrow>
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: colors.slateBlue, lineHeight: 1.45, maxWidth: 340 }}>
                Doodles add personality but don't carry the design. Components must feel Pixley without decoration. Stroke width: 2px. Max 1–2 doodles per card.
              </p>
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                {["#0065F8", "#00CAFF", "#FFD76A", "#FF6B9A"].map((c) => (
                  <div key={c} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 32, height: 4, borderRadius: 2, background: c }} />
                    <span style={{ fontSize: 10, color: colors.slateBlue }}>{c}</span>
                  </div>
                ))}
              </div>

              {/* Doodle rules */}
              <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                {[
                  "Stroke: 2px",
                  "Opacity: 70-100%",
                  "Near card corners",
                  "Max 2 per card",
                ].map((rule) => (
                  <span key={rule} style={{ fontSize: 11, fontWeight: 600, color: colors.slateBlue, background: "rgba(255,255,255,0.6)", padding: "4px 10px", borderRadius: 8 }}>
                    {rule}
                  </span>
                ))}
              </div>
            </div>

            {/* Decorative handwritten note */}
            <div
              style={{
                position: "absolute",
                bottom: 20,
                left: 24,
                fontFamily: "'Caveat', 'Patrick Hand', cursive",
                fontSize: 18,
                color: colors.pixleyBlue,
                fontWeight: 700,
                transform: "rotate(-1.5deg)",
              }}
            >
              Create, Imagine, Share!
            </div>

            {/* Decorative doodle strokes */}
            <svg width="120" height="80" viewBox="0 0 120 80" fill="none" style={{ position: "absolute", bottom: 10, right: 20, opacity: 0.6 }}>
              <path d="M10 70 Q 40 10 80 40 T 110 20" stroke="#00CAFF" strokeWidth="2" fill="none" strokeLinecap="round" />
              <path d="M20 60 Q 50 30 90 50" stroke="#FFD76A" strokeWidth="2" fill="none" strokeLinecap="round" />
              <circle cx="100" cy="15" r="6" stroke="#FF6B9A" strokeWidth="2" fill="none" />
              <path d="M5 40 L15 30 L25 40" stroke="#0065F8" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </BentoCard>

          {/* ── MOTION ─── span 4 (interactive) ───────────── */}
          <BentoCard
            id="ds-motion"
            fill={cardFills.cream}
            style={{ gridColumn: "span 4", minHeight: 200 }}
          >
            <Eyebrow>Motion</Eyebrow>
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: colors.slateBlue, lineHeight: 1.45, margin: "0 0 12px" }}>
                Motion reinforces tactility. Hover/press states on all interactive elements.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { label: "Button", val: "hover: translateY(-1px), pressed: translateY(4px)" },
                  { label: "Card", val: "hover: translateY(-3px), pressed: translateY(1px)" },
                  { label: "Sticker", val: "hover: rotate(0deg) scale(1.03)" },
                  { label: "Easing", val: "cubic-bezier(.2, .8, .2, 1)" },
                  { label: "Duration", val: "120ms buttons, 180ms cards, 160ms stickers" },
                ].map(({ label, val }) => (
                  <div key={label} style={{ display: "flex", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: colors.inkNavy, minWidth: 60 }}>{label}</span>
                    <span style={{ fontSize: 12, color: colors.slateBlue, fontFamily: "monospace" }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </BentoCard>

          {/* ── SPACING SCALE ─── span 4 ──────────────────── */}
          <BentoCard
            fill={cardFills.mint}
            style={{ gridColumn: "span 4", minHeight: 200 }}
          >
            <Eyebrow>Spacing Scale</Eyebrow>
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { token: "space-1", px: 4 },
                { token: "space-2", px: 8 },
                { token: "space-3", px: 12 },
                { token: "space-4", px: 16 },
                { token: "space-6", px: 24 },
                { token: "space-8", px: 32 },
                { token: "space-12", px: 48 },
                { token: "space-16", px: 64 },
              ].map(({ token, px }) => (
                <div key={token} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: colors.slateBlue, width: 65, fontFamily: "monospace" }}>{token}</span>
                  <div style={{ width: px, height: 12, borderRadius: 4, background: colors.pixleyBlue, opacity: 0.7, transition: "width 200ms ease" }} />
                  <span style={{ fontSize: 11, color: colors.slateBlue }}>{px}px</span>
                </div>
              ))}
            </div>
          </BentoCard>

          {/* ── RADII ─── span 4 ──────────────────────────── */}
          <BentoCard
            fill={cardFills.softPink}
            style={{ gridColumn: "span 4", minHeight: 200 }}
          >
            <Eyebrow>Radii</Eyebrow>
            <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 12 }}>
              {[
                { label: "Page", r: 28 },
                { label: "Card", r: 24 },
                { label: "Small Card", r: 20 },
                { label: "Button", r: 18 },
                { label: "Input", r: 14 },
                { label: "Modal", r: 28 },
                { label: "Pill", r: 999 },
              ].map(({ label, r }) => (
                <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: Math.min(r, 24),
                      border: `2px solid ${colors.pixleyBlue}`,
                      background: "rgba(0,101,248,0.06)",
                    }}
                  />
                  <span style={{ fontSize: 10, fontWeight: 700, color: colors.slateBlue }}>{r}px</span>
                  <span style={{ fontSize: 9, color: colors.slateBlue }}>{label}</span>
                </div>
              ))}
            </div>
          </BentoCard>

          {/* ── ELEVATION ─── span 6 ──────────────────────── */}
          <BentoCard
            fill={cardFills.paleBlue}
            style={{ gridColumn: "span 6", minHeight: 180 }}
          >
            <Eyebrow>Elevation & Depth</Eyebrow>
            <div style={{ display: "flex", gap: 20, marginTop: 16 }}>
              {[
                { label: "Default", shadow: cardShadow },
                { label: "Raised", shadow: raisedShadow },
                { label: "Pressed", shadow: "0px 1px 0px rgba(11,26,51,0.08), 0px 6px 16px rgba(11,26,51,0.10)", transform: "translateY(2px)" },
              ].map(({ label, shadow, transform }) => (
                <div key={label} style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      height: 80,
                      borderRadius: 20,
                      background: cardFills.cream,
                      boxShadow: shadow,
                      transform: transform || "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: colors.slateBlue }}>{label}</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: colors.slateBlue, marginTop: 8, display: "block" }}>{label} Card</span>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 12, color: colors.slateBlue, marginTop: 16, lineHeight: 1.4 }}>
              Avoid thin gray borders. Use color-tinted borders if needed: <code style={{ background: "rgba(0,101,248,0.08)", padding: "2px 6px", borderRadius: 4, fontSize: 11 }}>rgba(0,101,248,0.18)</code>
            </p>
          </BentoCard>

          {/* ── GUARDRAILS ─── span 6 ─────────────────────── */}
          <BentoCard
            fill={cardFills.cream}
            style={{ gridColumn: "span 6", minHeight: 180 }}
          >
            <Eyebrow>Implementation Guardrails</Eyebrow>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#E53E3E", margin: "0 0 8px" }}>Banned</p>
                <ul style={{ fontSize: 12, color: colors.slateBlue, lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                  <li>Pure white bento cards on white bg</li>
                  <li>Uniform 3-column grid</li>
                  <li>Thin gray border separators</li>
                  <li>Flat buttons without shadow</li>
                  <li>40px height gray-border inputs</li>
                  <li>Outline-only badges</li>
                </ul>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#20A96A", margin: "0 0 8px" }}>Required Feel</p>
                <ul style={{ fontSize: 12, color: colors.slateBlue, lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                  <li>Tactile</li>
                  <li>Playful</li>
                  <li>Modern</li>
                  <li>Generous</li>
                  <li>Color-confident</li>
                  <li>Parent-trustworthy</li>
                  <li>Kid-creative</li>
                </ul>
              </div>
            </div>
          </BentoCard>
        </div>

        {/* ── FOOTER ──────────────────────────────────────── */}
        <div
          style={{
            marginTop: 32,
            borderRadius: 24,
            background: colors.softWhite,
            boxShadow: cardShadow,
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/assets/pixley-panda.png" alt="Pixley" style={{ width: 28, height: 28, borderRadius: 8 }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: colors.inkNavy, margin: 0 }}>Pixley Design System</p>
              <p style={{ fontSize: 11, color: colors.slateBlue, margin: 0 }}>Made with love for young creators</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#3EDC8F" }} />
            <span style={{ fontSize: 12, color: colors.slateBlue }}>All systems operational</span>
          </div>
          <div style={{ fontSize: 12, color: colors.slateBlue }}>
            <span style={{ fontWeight: 700 }}>Version</span> v1.0.0
          </div>
          <button style={{ ...primaryBtnStyle, height: 36, fontSize: 12, padding: "0 16px" }}>
            Share Feedback
          </button>
        </div>
      </main>

      {/* ── Full Modal Overlay ────────────────────────────── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(11,26,51,0.42)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: cardFills.cream,
              borderRadius: 28,
              padding: 28,
              boxShadow: "0px 5px 0px rgba(11,26,51,0.12), 0px 24px 64px rgba(11,26,51,0.24)",
              maxWidth: 420,
              width: "90%",
              position: "relative",
            }}
          >
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 36,
                height: 36,
                borderRadius: 12,
                background: colors.softWhite,
                border: "none",
                cursor: "pointer",
                boxShadow: "0px 3px 0px rgba(11,26,51,0.14)",
                fontSize: 16,
                color: colors.slateBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {"\u2715"}
            </button>
            <h3
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 0.95,
                color: colors.inkNavy,
                margin: "0 0 12px",
                textTransform: "uppercase",
              }}
            >
              Amazing job!
            </h3>
            <p style={{ fontSize: 15, color: colors.slateBlue, lineHeight: 1.45, margin: "0 0 24px" }}>
              Your project has been published and shared with the world. Keep creating amazing things!
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button style={{ ...primaryBtnStyle, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                View Project
              </button>
              <button onClick={() => setShowModal(false)} style={{ ...secondaryBtnStyle, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');

        /* Neue Montreal approximation — use Inter as fallback */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        * {
          font-family: 'Inter', 'Neue Montreal', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* Burbank approximation — use Fredoka as closest Google Fonts match */
        @font-face {
          font-family: 'Burbank Big Condensed';
          src: local('Burbank Big Condensed'), local('Fredoka');
          font-weight: 700;
          font-display: swap;
        }

        /* Scrollbar styling */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #D7E3F3; border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover { background: #7FC6FF; }

        /* Interactive button hover */
        button:hover:not(:disabled) { filter: brightness(1.03); }
        button:active:not(:disabled) { filter: brightness(0.97); }

        /* Input focus */
        input:focus, textarea:focus, select:focus {
          border-color: #0065F8 !important;
          box-shadow: inset 0px 2px 4px rgba(11,26,51,0.04), 0px 0px 0px 4px rgba(0,101,248,0.14), 0px 2px 0px rgba(0,101,248,0.20) !important;
        }

        /* Responsive */
        @media (max-width: 1199px) {
          main > div[style*="grid-template-columns: repeat(12"] {
            grid-template-columns: repeat(8, 1fr) !important;
          }
          main > div[style*="grid-template-columns: repeat(12"] > div[style*="span 6"] {
            grid-column: span 8 !important;
          }
        }

        @media (max-width: 899px) {
          aside { display: none !important; }
          main { margin-left: 0 !important; }
          main > div[style*="grid-template-columns: repeat(12"] {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          main > div[style*="grid-template-columns: repeat(12"] > div {
            grid-column: span 4 !important;
          }
        }

        @media (max-width: 639px) {
          main > div[style*="grid-template-columns: repeat(12"] {
            grid-template-columns: 1fr !important;
          }
          main > div[style*="grid-template-columns: repeat(12"] > div {
            grid-column: span 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DesignSystem;
